'use client';

import {
  EntityRatingCommentCount,
  Professional,
  ProfessionalSchedule,
  ProfessionalScheduleProcedures,
} from '@medprev-web-app/api-graphql-client';
import { useCallback } from 'react';

import { ProfessionalAsideCard } from './professional-aside-card.component';
import { ProfessionalCommentsSort } from './professional-comments-sort.component';
import { ProfessionalHeader } from './professional-header.component';
import { ProfessionalScheduleRedirectButton } from './professional-schedule-redirect-button.component';
import { ProfessionalSpecialtiesAndLicense } from './professional-specialties-and-license.component';
import { ProfessionalProfileLoading } from './professional-profile-loading.component';
import { TabProfessional } from './tab-professional.component';

import { EntityProcedureAnchorButton } from '@/app/(main)/(pages)/_components/entities/entity-procedure/_components/entity-procedure-anchor-button.component';
import { EntitySpecialtiesAndRqe } from '@/app/(main)/(pages)/_components/entities/entity-specialties-and-rqe.component';
import { mapProfessionalLicenseRows } from '@/utils/professional/professional-license.util';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { RatingComment } from '@/types';
import { Breadcrumb } from '@/components/ui/breadcrumb.component';
import { professionalDisplayName } from '@/utils/formatters/professional-name';
import { Card } from '@/components/ui/card.component';
import { ContentContainer } from '@/components/ui/content-container.component';
import { ErrorBoundary } from '@/app/_components/error-boundary.component';
import ProfessionalNotFound from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/[slug]/_components/professional-not-found.component';
import { ProfessionalSchedulesSection } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/[slug]/_components/professional-schedules-section.component';
import { useNewSchedule } from '@/feature-flags/hooks/use-new-schedule.hook';
import { useProfessionalComments } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/[slug]/_hooks/use-professional-comments.hook';
import { useProfessionalProcedures } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/[slug]/_hooks/use-professional-procedures.hook';
import { useProfessionalProfile } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/[slug]/_hooks/use-professional-profile.hook';
import { getProfessionalProfileComments } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/_services/get-professional-profile-from-search-api.service';

interface ProfessionalWrapperProps {
  slug: string;
  professional: Professional;
  commentsCount?: EntityRatingCommentCount | null;
  procedures: ProfessionalScheduleProcedures[];
  inPersonData: ProfessionalSchedule[];
  telehealthData: ProfessionalSchedule[];
  initialComments?: RatingComment[];
}

export function ProfessionalWrapper({
  slug,
  professional: professionalFromBff,
  commentsCount: commentsCountFromBff,
  procedures,
  inPersonData,
  telehealthData,
  initialComments: initialCommentsFromBff,
}: ProfessionalWrapperProps) {
  const { enabled: newScheduleEnabled, loading: isFlagLoading } = useNewSchedule();
  const usesNewSchedule = !isFlagLoading && newScheduleEnabled;
  const { profile, status } = useProfessionalProfile(slug, usesNewSchedule, {
    professional: professionalFromBff,
    commentsCount: commentsCountFromBff,
    inPersonData,
    telehealthData,
    initialComments: initialCommentsFromBff,
  });
  const professionalId = String(profile?.professional.id ?? '');
  const professionalProcedures = useProfessionalProcedures(professionalId, {
    enabled: usesNewSchedule && Boolean(professionalId),
  });

  const {
    orderBy,
    comments,
    hasMore,
    commentsKey,
    isSorting,
    changeOrderBy,
  } = useProfessionalComments(
    professionalId,
    profile?.initialComments ?? [],
    Boolean(profile?.hasMoreComments),
  );

  const loadCommentsPage = useCallback(
    (page: number) => getProfessionalProfileComments(professionalId, page, orderBy),
    [professionalId, orderBy],
  );

  if (status === 'loading') {
    return <ProfessionalProfileLoading />;
  }

  if (status === 'error' || !profile) {
    return <ProfessionalNotFound />;
  }

  const {
    professional,
    gender,
    commentsCount,
    inPersonSchedules,
    hasTelehealth,
  } = profile;
  const hasSchedulesSection = professionalProcedures.isLoading || professionalProcedures.availableChannels.length > 0;

  const specialties = usesNewSchedule
    ? (
        <EntitySpecialtiesAndRqe
          professional={{
            licenses: mapProfessionalLicenseRows(professional),
            gender: gender ?? undefined,
            honorific: professional.honorific ?? undefined,
          }}
        />
      )
    : <ProfessionalSpecialtiesAndLicense professional={professional} />;

  const scheduleAction = usesNewSchedule
    ? hasSchedulesSection && (
      <EntityProcedureAnchorButton
        className="flex-1 min-w-0 h-10 text-base"
        data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_SELECT_REDIRECT}
      />
    )
    : <ProfessionalScheduleRedirectButton professionalId={professionalId} className="flex-1 min-w-0 h-10 text-base" />;

  const breadcrumbsItems = [
    {
      label: 'Profissionais',
      href: '/profissionais/',
    },
    {
      label: professionalDisplayName({ honorific: professional.honorific ?? '', name: professional.name ?? '' }),
    },
  ];

  return (
    <>
      <Breadcrumb list={breadcrumbsItems} abbreviated />

      <ErrorBoundary errorMessage="Professional Wrapper">
        <ContentContainer className="px-0 sm:px-0 min-[768px]:px-24 min-[768px]:py-6">
          <div className="xl:grid xl:grid-cols-2 xl:items-start xl:gap-6">
            <section>
              <Card className="w-full rounded-none border-0 p-6 sm:px-16 min-[768px]:rounded-xxl min-[768px]:border min-[768px]:p-6">
                <ProfessionalHeader
                  professional={professional}
                  commentsCount={commentsCount}
                  shouldAddLightbox
                  specialties={specialties}
                  scheduleAction={scheduleAction}
                />
                <TabProfessional
                  professional={professional}
                  countComments={commentsCount}
                  inPersonSchedules={inPersonSchedules}
                  hasTelehealth={hasTelehealth}
                  initialComments={comments}
                  hasMoreComments={hasMore}
                  loadCommentsPage={usesNewSchedule ? loadCommentsPage : undefined}
                  commentsKey={usesNewSchedule ? commentsKey : undefined}
                  isSortingComments={usesNewSchedule && isSorting}
                  commentsSort={usesNewSchedule && (
                    <ProfessionalCommentsSort
                      orderBy={orderBy}
                      onOrderByChange={changeOrderBy}
                      disabled={isSorting}
                    />
                  )}
                  schedulesSection={usesNewSchedule && (
                    <ProfessionalSchedulesSection
                      procedures={professionalProcedures}
                      className="rounded-none border-0 px-0 py-4"
                    />
                  )}
                />
              </Card>
            </section>

            {usesNewSchedule
              ? (
                  <ProfessionalSchedulesSection
                    procedures={professionalProcedures}
                    className="hidden w-full xl:sticky xl:top-6 xl:block"
                    isScrollTarget={false}
                  />
                )
              : (
                  <ProfessionalAsideCard
                    professional={professional}
                    procedure={procedures}
                    className="hidden xl:block"
                  />
                )}
          </div>
        </ContentContainer>
      </ErrorBoundary>
    </>
  );
}
